import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import MuiAlert from "../utils/muiAlerts";
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

function FileDropzone({ uploadedFiles, onFilesChange, maxFiles, maxSize = 10 * 1024 * 1024, accept }) {
    const theme = useTheme();
    const onDrop = useCallback((acceptedFiles) => {
        const newFiles = [...uploadedFiles, ...acceptedFiles].slice(0, maxFiles);
        onFilesChange(newFiles);
    }, [uploadedFiles, onFilesChange, maxFiles]);

    const onDropRejected = useCallback((fileRejections) => {
        if (!fileRejections.length) return;

        // Build a formatted error list
        const formattedErrors = fileRejections.map(({ file, errors }, idx) => {
            const errorMessages = errors
                .map(err => {
                    // Clean up hyphenated error codes into readable text
                    let readable = err.message || err.code.replace(/-/g, " ");
                    // Add a bit more context where needed
                    if (err.code === "file-too-large") {
                        readable = `File too large (limit: ${Math.round(maxSize / 1024 / 1024)} MB)`;
                    } else if (err.code === "too-many-files") {
                        readable = `Too many files (max: ${maxFiles})`;
                    } else if (err.code === "file-invalid-type") {
                        readable = `Invalid file type (only JPG/PNG allowed)`;
                    }
                    return readable;
                })
                .join(", ");

            return `<li><b>${idx + 1}. ${file.name}</b> — ${errorMessages}</li>`;
        });

        MuiAlert.fire({
            icon: "error",
            title: "Some files couldn’t be uploaded",
            html: `<ul style="text-align:left; margin-left:20px;">${formattedErrors.join("")}</ul>`,
            background: "#1e1e1e",
            color: "#fff",
            confirmButtonColor: "#28a745",
            width: 600,
        });
    }, [maxFiles, maxSize]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        onDropRejected,
        accept,
        maxFiles,
        maxSize,
    });

    const removeFile = (index) => {
        const newList = uploadedFiles.filter((_, i) => i !== index);
        onFilesChange(newList);
    };

    const showPreview = (file) => {
        const previewUrl = URL.createObjectURL(file);
        MuiAlert.fire({
            title: file.name,
            imageUrl: previewUrl,
            imageAlt: file.name,
            showCloseButton: true,
            showConfirmButton: false,
            width: 600,
            background: "#1e1e1e",
            color: "#fff",
            didClose: () => URL.revokeObjectURL(previewUrl),
        });
    };

    return (
        <Box>
            {uploadedFiles.length < maxFiles && (
                <Box
                    {...getRootProps()}
                    sx={{
                        border: '2px dashed',
                        borderColor: 'success.main',
                        borderRadius: 2,
                        p: 2.5,
                        minHeight: 150,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.05)'
                    }}
                >
                    <input {...getInputProps()} />
                    <Typography align="center" sx={{ fontWeight: 700 }}>
                        Drag 'n' drop some files here, or click to select files
                        <Typography component="span" variant="body2" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                            (Max {maxFiles} file{maxFiles > 1 ? "s" : ""}, up to {Math.round(maxSize / 1024 / 1024)} MB each)
                        </Typography>
                    </Typography>
                </Box>
            )}

            {uploadedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {uploadedFiles.map((file, i) => (
                            <Box
                                key={i}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    width: 150,
                                    textAlign: 'center',
                                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                                    {file.name}
                                </Typography>
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                                    {file.type.startsWith("image/") && (
                                        <IconButton size="small" color="info" title="View" onClick={() => showPreview(file)}>
                                            <VisibilityRoundedIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <Button size="small" variant="outlined" color="error" title="Remove" onClick={() => removeFile(i)}>
                                        <CloseRoundedIcon fontSize="small" />
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default FileDropzone;
